import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import type { RowOrigin } from '@/lib/reference'
import { RowFormDialog, type FieldValues } from './RowFormDialog'
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog'

export type FilterKind = 'text' | 'select' | 'none'
export type EditorKind = 'text' | 'number' | 'select' | 'readonly'

export interface Column<Row> {
  /** Field name. Doubles as the key of the value handed back by the form. */
  key: string
  header: string
  /** Value used for search, sort and filtering. */
  value: (row: Row) => string | number
  /** Cell content. Defaults to the search value. */
  render?: (row: Row) => ReactNode
  /** Per-column filter control. Defaults to text. */
  filter?: FilterKind
  /** Options for a select filter or editor. Defaults to the distinct values. */
  options?: string[]
  /** Editor shown in the add/edit dialog. Omitted columns are not editable. */
  editor?: EditorKind
  /** Raw value the editor starts from, when it differs from the display value. */
  edit?: (row: Row) => string
  /** Part of the natural key: settable on add, frozen afterwards. */
  isKey?: boolean
  /** Hard cap on the editor's input, for a field with a real length limit. */
  maxLength?: number
  numeric?: boolean
  /** Extra classes on the cell, mainly column width. */
  cellClassName?: string
  /**
   * Kept out of the grid but still searched and still in the add/edit form.
   *
   * For a field too long to give a column to — a free-text note — where
   * dropping it outright would mean an edit silently wiped it.
   */
  hidden?: boolean
}

interface RowLike {
  id: string
  rowOrigin: RowOrigin
  updatedAt: string | null
  updatedBy: string | null
}

interface ReferenceTableProps<Row extends RowLike> {
  columns: Column<Row>[]
  rows: Row[]
  /** Singular noun used in dialog titles, e.g. "Airport". */
  entity: string
  canEdit: boolean
  onCreate?: (values: FieldValues) => Promise<void>
  onUpdate?: (id: string, values: FieldValues) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  /** Non-blocking data problem on a row, e.g. a zone nobody knows. */
  rowWarning?: (row: Row) => string | null
  /**
   * Rows that may not be edited or deleted, whatever the caller's rights.
   *
   * For a row whose content is derived rather than entered — a built-in
   * group computes its members from the tables — where an edit would look
   * accepted and then be ignored.
   */
  rowLocked?: (row: Row) => boolean
  /** Called when a row is clicked. Turns rows into a selectable list. */
  onSelect?: (row: Row) => void
  selectedId?: string | null
  /** Controls placed left of the search box. */
  toolbar?: ReactNode
  /**
   * Column to sort by before the user picks one. `null` keeps the order the
   * rows arrive in, for a table whose default order is a multi-column one the
   * header cannot express.
   */
  defaultSortKey?: string | null
}

const PAGE_SIZES = [25, 50, 100]

function filterKindOf<Row>(column: Column<Row>): FilterKind {
  return column.filter ?? 'text'
}

/**
 * The reference-data grid: search, per-column filters, sort, paging, and
 * add / edit / delete for whoever holds `settings.edit`.
 *
 * Filtering and sorting run in the browser over the whole table. That is a
 * deliberate limit of the local provider — 8.672 airports is small enough to
 * hold in memory, and it keeps typing instant. A server-backed provider will
 * push the same predicates down; the props do not change when it does.
 */
export function ReferenceTable<Row extends RowLike>({
  columns,
  rows,
  entity,
  canEdit,
  onCreate,
  onUpdate,
  onDelete,
  rowWarning,
  rowLocked,
  onSelect,
  selectedId,
  toolbar,
  defaultSortKey,
}: ReferenceTableProps<Row>) {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [sortKey, setSortKey] = useState<string>(
    defaultSortKey === undefined ? (columns[0]?.key ?? '') : (defaultSortKey ?? ''),
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
  const [editing, setEditing] = useState<Row | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Row | null>(null)

  // Distinct values per select column, computed over the unfiltered table.
  const selectOptions = useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const column of columns) {
      if (filterKindOf(column) !== 'select') continue
      if (column.options) {
        out[column.key] = column.options
        continue
      }
      const seen = new Set<string>()
      for (const row of rows) seen.add(String(column.value(row)))
      out[column.key] = [...seen].sort((a, b) => a.localeCompare(b))
    }
    return out
  }, [columns, rows])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const active = Object.entries(filters).filter(([, value]) => value !== '')

    const matched = rows.filter((row) => {
      for (const [key, wanted] of active) {
        const column = columns.find((item) => item.key === key)
        if (!column) continue
        const value = String(column.value(row))
        if (filterKindOf(column) === 'select') {
          if (value !== wanted) return false
        } else if (!value.toLowerCase().includes(wanted.toLowerCase())) {
          return false
        }
      }
      if (!needle) return true
      return columns.some((column) => String(column.value(row)).toLowerCase().includes(needle))
    })

    const column = columns.find((item) => item.key === sortKey)
    if (column) {
      const direction = sortDir === 'asc' ? 1 : -1
      matched.sort((a, b) => {
        const left = column.value(a)
        const right = column.value(b)
        if (typeof left === 'number' && typeof right === 'number') {
          return (left - right) * direction
        }
        return String(left).localeCompare(String(right)) * direction
      })
    }
    return matched
  }, [rows, columns, search, filters, sortKey, sortDir])

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const currentPage = Math.min(page, pageCount - 1)
  const pageRows = visible.slice(currentPage * pageSize, currentPage * pageSize + pageSize)

  useEffect(() => setPage(0), [search, filters, pageSize])

  const filtersActive = Object.values(filters).some((value) => value !== '') || search !== ''

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir((value) => (value === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const editable = columns.filter((column) => column.editor && column.editor !== 'readonly')
  // Hidden columns stay out of the grid but keep their place in search and
  // in the form, so `columns` is the full set and `shown` is what is drawn.
  const shown = columns.filter((column) => !column.hidden)
  const rowActions = canEdit && Boolean(onUpdate || onDelete)
  // Warnings live in the trailing cell, which therefore has to exist even for
  // a reader who cannot edit anything.
  const hasTrailing = rowActions || Boolean(rowWarning)

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        {toolbar}

        <label className="relative min-w-[200px] flex-1">
          <span className="sr-only">Search</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"
          >
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="t-body-sm h-11 w-full rounded-[100px] border border-transparent
                       bg-surface-strong pl-10 pr-4 text-ink outline-none
                       placeholder:text-muted-soft focus:border-primary"
          />
        </label>

        {filtersActive && (
          <Button
            variant="tertiary"
            onClick={() => {
              setSearch('')
              setFilters({})
            }}
          >
            Clear
          </Button>
        )}

        {canEdit && onCreate && (
          <Button onClick={() => setEditing('new')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            Add
          </Button>
        )}
      </div>

      <div className="mt-4 overflow-x-auto rounded-[16px] border border-hairline">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-hairline bg-surface-soft">
              {shown.map((column) => {
                const sorted = sortKey === column.key
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={sorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`px-3 py-2.5 align-bottom ${column.cellClassName ?? ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="t-caption-strong flex items-center gap-1 whitespace-nowrap text-ink"
                    >
                      {column.header}
                      {sorted && (
                        <span className="text-primary" aria-hidden="true">
                          {sortDir === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </button>
                  </th>
                )
              })}
              {hasTrailing && <th scope="col" className="w-px px-3" />}
            </tr>

            {/* The filter control sets each column's floor, so its minimum is
                what decides whether a wide table has to scroll sideways. */}
            <tr className="border-b border-hairline bg-surface-soft">
              {shown.map((column) => {
                const kind = filterKindOf(column)
                const value = filters[column.key] ?? ''
                return (
                  <th key={column.key} scope="col" className="px-3 pb-2.5">
                    {kind === 'none' ? null : kind === 'select' ? (
                      <select
                        aria-label={`Filter ${column.header}`}
                        value={value}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, [column.key]: event.target.value }))
                        }
                        className="t-body-sm h-8 w-full min-w-[64px] rounded-[8px] border border-hairline
                                   bg-canvas px-2 text-ink outline-none focus:border-primary"
                      >
                        <option value="">All</option>
                        {(selectOptions[column.key] ?? []).map((option) => (
                          <option key={option} value={option}>
                            {option === '' ? '—' : option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        aria-label={`Filter ${column.header}`}
                        value={value}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, [column.key]: event.target.value }))
                        }
                        className="t-body-sm h-8 w-full min-w-[64px] rounded-[8px] border border-hairline
                                   bg-canvas px-2 text-ink outline-none focus:border-primary"
                      />
                    )}
                  </th>
                )
              })}
              {hasTrailing && <th className="px-3 pb-2.5" />}
            </tr>
          </thead>

          <tbody>
            {pageRows.map((row) => {
              const warning = rowWarning?.(row) ?? null
              const locked = rowLocked?.(row) ?? false
              const selected = selectedId === row.id
              return (
                <tr
                  key={row.id}
                  onClick={onSelect ? () => onSelect(row) : undefined}
                  className={[
                    'border-b border-hairline-soft last:border-0',
                    onSelect ? 'cursor-pointer' : '',
                    selected ? 'bg-surface-strong' : 'hover:bg-surface-soft',
                  ].join(' ')}
                >
                  {shown.map((column) => (
                    <td
                      key={column.key}
                      className={`px-3 py-2.5 align-middle ${
                        column.numeric ? 't-number-sm text-ink' : 't-body-sm text-body'
                      } ${column.cellClassName ?? ''}`}
                    >
                      {column.render ? column.render(row) : String(column.value(row))}
                    </td>
                  ))}

                  {hasTrailing && (
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {warning && (
                          <span className="t-caption-strong text-semantic-down" title={warning}>
                            !
                          </span>
                        )}
                        {row.rowOrigin !== 'seed' && (
                          <span className="t-caption-strong rounded-[100px] bg-surface-strong px-2 py-0.5 text-muted">
                            Local
                          </span>
                        )}
                        {rowActions && onUpdate && !locked && (
                          <IconButton
                            label={`Edit ${column0Value(columns, row)}`}
                            onClick={(event) => {
                              event.stopPropagation()
                              setEditing(row)
                            }}
                          >
                            <path
                              d="M10.5 2.8 13.2 5.5 5.7 13H3v-2.7l7.5-7.5Z"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinejoin="round"
                            />
                          </IconButton>
                        )}
                        {rowActions && onDelete && !locked && (
                          <IconButton
                            label={`Delete ${column0Value(columns, row)}`}
                            tone="danger"
                            onClick={(event) => {
                              event.stopPropagation()
                              setDeleting(row)
                            }}
                          >
                            <path
                              d="M3.5 4.5h9M6.5 4.5V3h3v1.5M5 4.5l.6 8.5h4.8l.6-8.5"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </IconButton>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              )
            })}

            {pageRows.length === 0 && (
              <tr>
                <td
                  colSpan={shown.length + (hasTrailing ? 1 : 0)}
                  className="t-body-sm px-3 py-10 text-center text-muted"
                >
                  {rows.length === 0 ? 'No rows' : 'No match'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="t-caption text-muted">
          <span className="t-number-sm text-ink">{visible.length.toLocaleString('en-GB')}</span>
          {visible.length === rows.length ? '' : ` of ${rows.length.toLocaleString('en-GB')}`}
        </p>

        <div className="flex items-center gap-2">
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="t-body-sm h-9 rounded-[8px] border border-hairline bg-canvas px-2 text-ink
                       outline-none focus:border-primary"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          <PagerButton label="Previous page" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
            <path d="M9 3.5 5 8l4 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </PagerButton>

          <p className="t-caption-strong min-w-[64px] text-center text-body">
            {currentPage + 1} / {pageCount}
          </p>

          <PagerButton
            label="Next page"
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            <path d="m7 3.5 4 4.5-4 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </PagerButton>
        </div>
      </div>

      {editing && (
        <RowFormDialog
          title={editing === 'new' ? `Add ${entity.toLowerCase()}` : `Edit ${entity.toLowerCase()}`}
          columns={editable.map((column) => ({
            key: column.key,
            header: column.header,
            editor: column.editor ?? 'text',
            options: column.options ?? selectOptions[column.key],
            maxLength: column.maxLength,
            /** A natural key can be chosen once and never rewritten. */
            frozen: editing !== 'new' && Boolean(column.isKey),
          }))}
          initial={
            editing === 'new'
              ? {}
              : Object.fromEntries(
                  editable.map((column) => [
                    column.key,
                    column.edit ? column.edit(editing) : String(column.value(editing)),
                  ]),
                )
          }
          onCancel={() => setEditing(null)}
          onSubmit={async (values) => {
            if (editing === 'new') await onCreate?.(values)
            else await onUpdate?.(editing.id, values)
            setEditing(null)
          }}
        />
      )}

      {deleting && (
        <ConfirmDeleteDialog
          entity={entity}
          label={column0Value(columns, deleting)}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await onDelete?.(deleting.id)
            setDeleting(null)
          }}
        />
      )}
    </div>
  )
}

/** First column's value — what a row is called in a dialog or a label. */
function column0Value<Row extends RowLike>(columns: Column<Row>[], row: Row): string {
  const first = columns[0]
  return first ? String(first.value(row)) : row.id
}

function IconButton({
  label,
  onClick,
  children,
  tone = 'default',
}: {
  label: string
  onClick: (event: MouseEvent) => void
  children: ReactNode
  tone?: 'default' | 'danger'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`rounded-[8px] p-1.5 transition-colors hover:bg-surface-strong active:bg-hairline
                  ${tone === 'danger' ? 'text-semantic-down' : 'text-body'}`}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        {children}
      </svg>
    </button>
  )
}

function PagerButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="rounded-[8px] border border-hairline p-2 text-body transition-colors
                 hover:bg-surface-soft active:bg-surface-strong
                 disabled:cursor-not-allowed disabled:text-muted-soft disabled:hover:bg-transparent"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        {children}
      </svg>
    </button>
  )
}
